import React from 'react';
import '../styles.css';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  animation?: 'pulse' | 'wave' | 'none';
}

/**
 * Base Skeleton component for loading states
 * Provides a simple, animated placeholder that matches content dimensions
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '1em',
  className = '',
  variant = 'rectangular',
  animation = 'pulse',
}) => {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const variantClass = variant === 'circular'
    ? 'skeleton-circular'
    : variant === 'text'
    ? 'skeleton-text'
    : 'skeleton-rectangular';

  const animationClass = animation === 'wave'
    ? 'skeleton-wave'
    : animation === 'pulse'
    ? 'skeleton-pulse'
    : '';

  return (
    <div
      className={`skeleton ${variantClass} ${animationClass} ${className}`}
      style={style}
      aria-busy="true"
      aria-live="polite"
    />
  );
};

interface SkeletonGroupProps {
  count?: number;
  spacing?: number;
  children?: React.ReactNode;
}

/**
 * SkeletonGroup component for rendering multiple skeletons
 */
export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({
  count = 3,
  spacing = 12,
  children,
}) => {
  if (children) {
    return (
      <div className="skeleton-group" style={{ gap: `${spacing}px` }}>
        {children}
      </div>
    );
  }

  return (
    <div className="skeleton-group" style={{ gap: `${spacing}px` }}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} />
      ))}
    </div>
  );
};

/**
 * SkeletonValueBetCard - Loading skeleton for Value Bet cards
 */
export const SkeletonValueBetCard: React.FC = () => {
  return (
    <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <Skeleton width="60%" height="20px" />
          <Skeleton width="80%" height="16px" style={{ marginTop: '8px' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <Skeleton width="60px" height="24px" variant="rectangular" />
          <Skeleton width="50px" height="24px" variant="rectangular" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <Skeleton width="100px" height="28px" variant="rectangular" />
        <Skeleton width="80px" height="28px" variant="rectangular" />
        <Skeleton width="90px" height="28px" variant="rectangular" />
      </div>
    </div>
  );
};

/**
 * SkeletonCLVStats - Loading skeleton for CLV statistics
 */
export const SkeletonCLVStats: React.FC = () => {
  return (
    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
      <Skeleton width="40%" height="20px" style={{ marginBottom: '16px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
        <div>
          <Skeleton width="80%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="28px" />
        </div>
        <div>
          <Skeleton width="80%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="28px" />
        </div>
        <div>
          <Skeleton width="80%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="28px" />
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonCard - Generic loading skeleton for cards
 */
export const SkeletonCard: React.FC = () => {
  return (
    <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
      <Skeleton width="50%" height="20px" style={{ marginBottom: '12px' }} />
      <Skeleton width="100%" height="16px" style={{ marginBottom: '8px' }} />
      <Skeleton width="90%" height="16px" style={{ marginBottom: '8px' }} />
      <Skeleton width="80%" height="16px" />
    </div>
  );
};

/**
 * SkeletonText - Text line skeleton
 */
export const SkeletonText: React.FC<{ width?: string | number }> = ({ width = '100%' }) => {
  return <Skeleton width={width} height="1em" variant="text" />;
};

/**
 * SkeletonRow - Table row skeleton
 */
export const SkeletonRow: React.FC = () => {
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <Skeleton width="30%" height="16px" />
      <Skeleton width="20%" height="16px" />
      <Skeleton width="25%" height="16px" />
      <Skeleton width="25%" height="16px" />
    </div>
  );
};

/**
 * SkeletonFixtureCard - Loading skeleton for fixture cards
 */
export const SkeletonFixtureCard: React.FC = () => {
  return (
    <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <Skeleton width="40%" height="16px" />
        <Skeleton width="20%" height="16px" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <Skeleton width="80%" height="20px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="16px" />
        </div>
        <Skeleton width="40px" height="40px" variant="rectangular" style={{ margin: '0 16px' }} />
        <div style={{ flex: 1, textAlign: 'right' }}>
          <Skeleton width="80%" height="20px" style={{ marginBottom: '8px', marginLeft: 'auto' }} />
          <Skeleton width="60%" height="16px" style={{ marginLeft: 'auto' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <Skeleton width="80px" height="32px" variant="rectangular" />
        <Skeleton width="80px" height="32px" variant="rectangular" />
        <Skeleton width="80px" height="32px" variant="rectangular" />
      </div>
    </div>
  );
};

/**
 * SkeletonMarketCard - Loading skeleton for market cards
 */
export const SkeletonMarketCard: React.FC = () => {
  return (
    <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
      <Skeleton width="60%" height="18px" style={{ marginBottom: '12px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
        <div style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <Skeleton width="100%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="20px" />
        </div>
        <div style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <Skeleton width="100%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="20px" />
        </div>
        <div style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <Skeleton width="100%" height="14px" style={{ marginBottom: '8px' }} />
          <Skeleton width="60%" height="20px" />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
